// components/common/OrganizationSkeleton.jsx
import React from 'react';

const Skeleton = ({ className = '', variant = 'shimmer' }) => {
  const baseClasses = `relative overflow-hidden bg-gray-200 rounded ${className}`;
  
  const getVariantClasses = () => {
    switch (variant) {
      case 'shimmer':
        return 'before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_ease-in-out_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent';
      case 'pulse':
        return 'animate-pulse';
      case 'wave':
        return 'bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[size:200%_100%] animate-[wave_2s_linear_infinite]';
      default:
        return 'animate-pulse';
    }
  };

  return <div className={`${baseClasses} ${getVariantClasses()}`} />;
};

export const OrganizationSkeleton = () => (
  <div className="min-h-screen bg-gray-50 animate-fadeIn">
    {/* Header Skeleton */}
    <div className="bg-white shadow-sm border-b sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12">
          <div className="flex items-center gap-4">
            <Skeleton className="w-10 h-10" variant="pulse" />
            <Skeleton className="h-7 w-48" variant="shimmer" />
          </div>
          <Skeleton className="w-20 h-10" variant="pulse" />
        </div>
      </div>
    </div>

    {/* Cover and Profile Skeleton */}
    <div className="relative">
      {/* Cover with gradient animation */}
      <div className="h-48 w-full bg-gradient-to-r from-red-200 via-pink-200 to-red-200 bg-[size:200%_100%] animate-[wave_3s_ease-in-out_infinite]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative -mt-24 pb-6">
          <div className="bg-white rounded-lg shadow-xl p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-end space-y-4 sm:space-y-0 sm:space-x-6">
              {/* Profile Picture Skeleton */}
              <div className="w-32 h-32 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-[shimmer_2s_ease-in-out_infinite]" />
              </div>
              
              <div className="flex-1 space-y-3">
                <Skeleton className="h-8 w-64" variant="shimmer" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-24" variant="pulse" />
                  <Skeleton className="h-6 w-20 rounded-full bg-green-100" variant="pulse" />
                  <Skeleton className="h-6 w-24 rounded-full bg-red-100" variant="pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Tabs Skeleton */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex overflow-x-auto p-1">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="opacity-0 animate-[fadeInUp_0.5s_ease-out_forwards]"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <Skeleton className="h-14 w-32 mr-2" variant="shimmer" />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Content Skeleton */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stats Cards with colored backgrounds */}
        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { bg: 'bg-blue-50', icon: 'bg-blue-100' },
            { bg: 'bg-green-50', icon: 'bg-green-100' },
            { bg: 'bg-purple-50', icon: 'bg-purple-100' },
            { bg: 'bg-red-50', icon: 'bg-red-100' }
          ].map((item, i) => (
            <div
              key={i}
              className="opacity-0 animate-[fadeInScale_0.5s_ease-out_forwards]"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div className={`bg-white rounded-lg shadow-sm p-6 relative overflow-hidden`}>
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24" variant="shimmer" />
                    <Skeleton className="h-8 w-16" variant="pulse" />
                  </div>
                  <div className={`w-12 h-12 rounded-lg ${item.icon} animate-pulse`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info Cards */}
        <div className="lg:col-span-2 space-y-6">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="opacity-0 animate-[fadeInLeft_0.5s_ease-out_forwards]"
              style={{ animationDelay: `${(i + 4) * 0.1}s` }}
            >
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <div className="w-5 h-5 rounded bg-red-200 mr-2 animate-pulse" />
                  <Skeleton className="h-6 w-48" variant="shimmer" />
                </div>
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="flex justify-between items-center py-3 border-b border-gray-100">
                      <Skeleton className="h-4 w-24" variant="pulse" />
                      <Skeleton className="h-4 w-32" variant="shimmer" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="lg:col-span-1">
          <div
            className="opacity-0 animate-[fadeInRight_0.5s_ease-out_forwards]"
            style={{ animationDelay: '0.6s' }}
          >
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 rounded bg-red-200 mr-2 animate-pulse" />
                <Skeleton className="h-6 w-36" variant="shimmer" />
              </div>
              <div className="space-y-4">
                {[
                  { color: 'bg-green-500' },
                  { color: 'bg-blue-500' }
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-1.5 ${item.color} animate-pulse`} />
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-32" variant="shimmer" />
                      <Skeleton className="h-3 w-24" variant="pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Add animation styles
if (typeof document !== 'undefined' && !document.getElementById('org-skeleton-styles')) {
  const style = document.createElement('style');
  style.id = 'org-skeleton-styles';
  style.textContent = `
    @keyframes shimmer {
      to { transform: translateX(100%); }
    }
    @keyframes wave {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes fadeInScale {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    @keyframes fadeInLeft {
      from {
        opacity: 0;
        transform: translateX(-20px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    @keyframes fadeInRight {
      from {
        opacity: 0;
        transform: translateX(20px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
  `;
  document.head.appendChild(style);
}

export default OrganizationSkeleton;
